import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Q, Pill, hoyGT } from "./util";

const FT = { Facturado: "green", Parcial: "amber", Pendiente: "zinc" };
const COLS = ["Pedido", "Cliente", "Dirección", "Zona", "Productos", "Precio prod.", "Envío", "Total", "Anticipo", "Cobros posteriores", "COD", "Saldo por cobrar", "Liquidación", "Formas de pago / bancos", "Estado", "Facturación"];
const NUM = ["precio_productos", "envio", "total", "anticipo", "cobros_posteriores", "cod"];

export const TodosHoja = () => {
  const h = hoyGT();
  const [desde, setDesde] = useState(`${h.slice(0, 8)}01`);
  const [hasta, setHasta] = useState(h);
  const [d, setD] = useState(null);
  const seq = useRef(0);
  const load = useCallback(() => {
    const n = ++seq.current;
    api.get("/mi-dinero/todos", { params: { desde, hasta } }).then(({ data }) => { if (n === seq.current) setD(data); }).catch((e) => toast.error(formatApiError(e)));
  }, [desde, hasta]);
  useEffect(() => { load(); }, [load]);
  const excel = async () => {
    try {
      const { data } = await api.get("/mi-dinero/todos.xlsx", { params: { desde, hasta }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data); a.download = `mi_dinero_todos_${desde}_${hasta}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const t = d?.totales || {};
  return (
    <div className="space-y-3" data-testid="todos-hoja">
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="todos-desde" />
        <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="todos-hasta" />
        <Button variant="outline" onClick={() => window.print()} className="ml-auto" data-testid="todos-imprimir"><Printer className="w-4 h-4 mr-1" />Imprimir</Button>
        <Button variant="outline" onClick={excel} data-testid="todos-excel"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
      </div>
      <div className="grid xl:grid-cols-[1fr_260px] gap-3 items-start">
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full text-[11px]">
            <thead className="bg-violet-700 text-white text-left"><tr>{COLS.map((c) => <th key={c} className="px-1.5 py-2">{c}</th>)}</tr></thead>
            <tbody className="divide-y">
              {(d?.pedidos || []).map((r) => (
                <tr key={r.numero} data-testid={`todos-fila-${r.numero}`}>
                  <td className="px-1.5 py-1 font-bold whitespace-nowrap">{r.numero}<div className="font-normal text-[10px]">{r.envio_tipo}</div></td>
                  <td className="px-1.5">{r.cliente}</td><td className="px-1.5 max-w-[180px]">{r.direccion}</td><td className="px-1.5">{r.zona}</td>
                  <td className="px-1.5 max-w-[180px]">{r.productos}</td>
                  {NUM.map((k) => <td key={k} className="px-1.5 text-right whitespace-nowrap"><Q v={r[k]} />{k === "anticipo" && r.anticipo_banco && <div className="text-[9px] text-zinc-500">{r.anticipo_banco}</div>}</td>)}
                  <td className="px-1.5 text-right font-black whitespace-nowrap"><Q v={r.a_cobrar} /></td>
                  <td className="px-1.5 text-[10px]">{r.liquidacion || "—"}</td>
                  <td className="px-1.5">{r.formas_pago || "—"}</td><td className="px-1.5 capitalize">{r.estado}</td>
                  <td className="px-1.5"><Pill tone={FT[r.facturacion]}>{r.facturacion}</Pill></td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-zinc-100 font-black" data-testid="todos-totales">
              <tr>
                <td className="px-1.5 py-2" colSpan={5}>TOTALES ({d?.pedidos?.length || 0} pedidos)</td>
                {[...NUM, "a_cobrar"].map((k) => <td key={k} className="px-1.5 text-right whitespace-nowrap"><Q v={t[k]} /></td>)}
                <td colSpan={4} />
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="rounded-xl border bg-white p-3" data-testid="todos-bancos">
          <div className="font-black text-sm mb-2">Resumen por bancos</div>
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-2 text-[10px] uppercase text-zinc-500 border-b pb-1"><span>Banco</span><span>Confirmado</span><span>Por verificar</span></div>
          {(d?.bancos || []).map((b) => (
            <div key={b.banco} className="grid grid-cols-[1fr_auto_auto] gap-x-2 text-xs py-1 border-b" data-testid={`todos-banco-${b.banco}`}>
              <span>{b.banco}</span><b className="text-emerald-700"><Q v={b.confirmado} /></b><span className="text-amber-700"><Q v={b.pendiente} /></span>
            </div>
          ))}
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-2 text-xs pt-2 font-black"><span>Total</span><Q v={d?.total_confirmado} className="text-emerald-700" /><Q v={d?.total_pendiente} className="text-amber-700" /></div>
          <div className="flex justify-between text-sm pt-1 font-black"><span>Recibido</span><Q v={d?.total_bancos} /></div>
          {d?.por_depositar_cod > 0 && <div className="flex justify-between text-xs pt-1 text-amber-700" data-testid="todos-por-depositar"><span>COD pendiente de depositar (aún no en banco)</span><b><Q v={d.por_depositar_cod} /></b></div>}
          <div className="text-[10px] text-zinc-500 mt-2">Dinero recibido en las fechas elegidas: anticipos, cobros de repartidor, liquidaciones COD y otros pagos. No depende de si está facturado.</div>
        </div>
      </div>
    </div>
  );
};
